// Button Import Path '../common/ui/Button' 
import React from 'react';
import { cva } from 'class-variance-authority';
import Tooltip from "../ui/Tooltip";

const buttonStyles = cva(
  'inline-flex items-center justify-center rounded-lg transition-colors font-bold cursor-pointer focus:outline-none',
  {
    variants: {
      color: {
        primary: '',
        secondary: '',
        tertiary: '',
        white: '',
        danger: '',
        success: '',
      },
      variant: {
        filled: '',
        outline: '',
        ghost: '',
      },
      size: {
        small: 'text-sm',
        medium: 'text-base',
        large: 'text-lg',
      },
      state: {
        default: '',
        loading: 'cursor-wait',
        locked: 'cursor-not-allowed',
      },
      isIconOnly: {
        true: 'py-2 px-4',
        false: '',
      }
    },
    compoundVariants: [
      // Size variants when not icon-only
      {
        isIconOnly: false,
        size: 'small',
        className: 'px-4 py-2',
      },
      {
        isIconOnly: false,
        size: 'medium',
        className: 'px-6 py-3',
      },
      {
        isIconOnly: false,
        size: 'large',
        className: 'px-8 py-4',
      },
      // Primary Colors
      {
        color: 'primary',
        variant: 'filled',
        className: 'bg-primary-base text-white hover:bg-primary-dark focus-visible:ring-2 focus-visible:ring-primary-base focus-visible:ring-offset-2 disabled:bg-primary-200',
      },
      {
        color: 'primary',
        variant: 'outline',
        className:
          'border border-primary-base text-primary-base hover:border-primary-dark hover:bg-primary-50 hover:text-primary-dark focus-visible:ring-2 focus-visible:ring-primary-base focus-visible:ring-offset-2 disabled:border-primary-200 disabled:text-primary-200',
      },
      {
        color: 'primary',
        variant: 'ghost',
        className:
          'border border-transparent bg-transparent text-primary-base hover:border-primary-base focus-visible:ring-2 focus-visible:ring-primary-base focus-visible:ring-offset-2 disabled:text-primary-200',
      },
      // Secondary Colors
      {
        color: 'secondary',
        variant: 'filled',
        className: 'bg-secondary-base text-white hover:bg-secondary-dark focus-visible:ring-2 focus-visible:ring-secondary-base focus-visible:ring-offset-2 disabled:bg-secondary-200',
      },
      {
        color: 'secondary',
        variant: 'outline',
        className:
          'border border-secondary-base text-secondary-base hover:border-secondary-dark hover:text-secondary-dark focus-visible:ring-2 focus-visible:ring-secondary-base focus-visible:ring-offset-2 disabled:border-secondary-200 disabled:text-secondary-200',
      },
      {
        color: 'secondary',
        variant: 'ghost',
        className:
          'border border-transparent bg-transparent text-secondary-base hover:border-secondary-base focus-visible:ring-2 focus-visible:ring-secondary-base focus-visible:ring-offset-2 disabled:text-secondary-200',
      },
      // Tertiary Colors
      {
        color: 'tertiary',
        variant: 'filled',
        className: 'bg-tertiary-base text-white hover:bg-tertiary-dark focus-visible:ring-2 focus-visible:ring-tertiary-base focus-visible:ring-offset-2 disabled:bg-tertiary-200',
      },
      {
        color: 'tertiary',
        variant: 'outline',
        className:
          'border border-tertiary-base text-tertiary-base hover:border-tertiary-dark hover:text-tertiary-dark focus-visible:ring-2 focus-visible:ring-tertiary-base focus-visible:ring-offset-2 disabled:border-tertiary-200 disabled:text-tertiary-200',
      },
      {
        color: 'tertiary',
        variant: 'ghost',
        className: 'bg-transparent text-tertiary-base hover:bg-tertiary-50 focus-visible:ring-2 focus-visible:ring-tertiary-base focus-visible:ring-offset-2 disabled:text-tertiary-200',
      },
      // White Colors
      {
        color: 'white',
        variant: 'filled',
        className: 'bg-white text-primary-base hover:bg-primary-100 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 disabled:bg-tertiary-200',
      },
      {
        color: 'white',
        variant: 'outline',
        className:
          'border border-white text-white hover:border-primary-100 hover:text-primary-100 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 disabled:border-tertiary-200 disabled:text-tertiary-200',
      },
      {
        color: 'white',
        variant: 'ghost',
        className:
          'border border-transparent bg-transparent text-white hover:border-white focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 disabled:text-greyscale-200',
      },
      // Danger Colors
      {
        color: 'danger',
        variant: 'filled',
        className: 'bg-alerts-error-base text-white hover:bg-alerts-error-dark focus-visible:ring-2 focus-visible:ring-alerts-error-base focus-visible:ring-offset-2 disabled:bg-alerts-error-light',
      },
      {
        color: 'danger',
        variant: 'outline',
        className:
          'border border-alerts-error-base text-alerts-error-base hover:border-alerts-error-dark hover:bg-alerts-error-light hover:text-alerts-error-dark focus-visible:ring-2 focus-visible:ring-alerts-error-base focus-visible:ring-offset-2 disabled:border-gray-200 disabled:text-gray-400',
      },
      {
        color: 'danger',
        variant: 'ghost',
        className:
          'border border-transparent bg-transparent text-alerts-error-base hover:bg-alerts-error-light focus-visible:ring-2 focus-visible:ring-alerts-error-base focus-visible:ring-offset-2 disabled:text-gray-400',
      },
      // Success Colors
      {
        color: 'success',
        variant: 'filled',
        className: 'bg-alerts-success-base text-white hover:bg-alerts-success-dark disabled:bg-alerts-success-light',
      },
      {
        color: 'success',
        variant: 'outline',
        className:
          'border border-alerts-success-base text-alerts-success-base hover:border-alerts-success-dark hover:bg-alerts-success-light hover:text-alerts-success-dark disabled:border-gray-200 disabled:text-gray-400',
      },
      {
        color: 'success',
        variant: 'ghost',
        className:
          'border border-transparent bg-transparent text-alerts-success-base hover:bg-alerts-success-light disabled:text-gray-400',
      },
    ],
    defaultVariants: {
      color: 'primary',
      variant: 'filled',
      size: 'medium',
      state: 'default',
      isIconOnly: false,
    },
  }
);

type ButtonColor = 'primary' | 'secondary' | 'tertiary' | 'white' | 'danger' | 'success';
type ButtonVariant = 'filled' | 'outline' | 'ghost';
type ButtonSize = 'small' | 'medium' | 'large';
type ButtonState = 'default' | 'loading' | 'locked';

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  color?: ButtonColor;
  variant?: ButtonVariant;
  size?: ButtonSize;
  state?: ButtonState;
  loading?: boolean;
  locked?: boolean;
  icon?: React.ReactNode;
  iconOnly?: boolean;
  className?: string;
  tooltipText?: string;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      color,
      variant,
      size,
      state,
      children,
      disabled,
      loading,
      locked,
      icon,
      iconOnly = false,
      tooltipText,
      ...props
    },
    ref
  ) => {
    // Determine button state, if a button is loading always use the State loading
    const buttonState = loading ? 'loading' : locked ? 'locked' : 'default';

    const buttonContent = (
      <div className="flex items-center justify-center relative">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex items-center space-x-1">
              <div className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" />
              <div className="w-1.5 h-1.5 rounded-full bg-current animate-bounce [animation-delay:0.2s]" />
              <div className="w-1.5 h-1.5 rounded-full bg-current animate-bounce [animation-delay:0.4s]" />
            </div>
          </div>
        )}
        <div className={loading ? 'opacity-0' : 'opacity-100'}>
          {locked ? (
            <div className="flex items-center gap-2">
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {/* Lock icon */}
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
              {children}
            </div>
          ) : (
            <div className="flex items-center">
              {icon && <span className={iconOnly ? '' : 'mr-2'}>{icon}</span>}
              {children}
            </div>
          )}
        </div>
      </div>
    );

    return locked ? (
      <Tooltip text={tooltipText || "You need to upgrade your plan to access this feature."}>
        <button
          className={buttonStyles({ color, variant, size, state: buttonState, isIconOnly: iconOnly, className })}
          disabled={true}
          ref={ref}
          {...props}
        >
          {buttonContent}
        </button>
      </Tooltip>
    ) : (
      <button
        className={buttonStyles({ color, variant, size, state: buttonState, isIconOnly: iconOnly, className })}
        disabled={disabled || loading}
        ref={ref}
        {...props}
      >
        {buttonContent}
      </button>
    );
  }
);

Button.displayName = 'Button';